import {
  Box, Grid, Typography, Divider, InputAdornment, ToggleButtonGroup, ToggleButton, alpha, useTheme
} from '@mui/material';
import { AttachMoney as MoneyIcon } from '@mui/icons-material';
import { SectionPaper, SectionTitle, TotalRow, NumberTextField, SummaryValue } from './styledComponents';
import { formatCurrency } from './utils';

const SummarySection = ({ invoice, onUpdate, canEdit = true }) => {
  const theme = useTheme();
  const discountType = invoice.discount_type || 'percent';

  return (
    <SectionPaper sx={{ width: '100%', mt: 3 }}>
      <SectionTitle variant="h6"><MoneyIcon />Summary</SectionTitle>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          {/* Subtotal */}
          <TotalRow>
            <Typography variant="body1" fontWeight={500}>Subtotal:</Typography>
            <SummaryValue variant="body1" fontWeight={500}>{formatCurrency(invoice.subtotal)}</SummaryValue>
          </TotalRow>

          {/* Discount */}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ mr: 1, fontWeight: 500 }}>Discount:</Typography>
              <NumberTextField
                type="number"
                value={invoice.discount || ''}
                onChange={(e) => canEdit && onUpdate({ discount: e.target.value })}
                InputProps={discountType === 'percent'
                  ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                  : { startAdornment: <InputAdornment position="start">$</InputAdornment> }
                }
                size="small" disabled={!canEdit}
                sx={{ width: '130px', opacity: canEdit ? 1 : 0.6 }}
                inputProps={{ min: 0, max: discountType === 'percent' ? 100 : undefined, step: discountType === 'percent' ? 0.1 : 0.01 }}
              />
              <ToggleButtonGroup
                value={discountType}
                exclusive
                size="small"
                disabled={!canEdit}
                onChange={(_, val) => { if (val && canEdit) onUpdate({ discount_type: val, discount: 0 }); }}
                sx={{ height: 32 }}
              >
                <ToggleButton value="percent" sx={{ px: 1.5, fontSize: '0.75rem', textTransform: 'none' }}>%</ToggleButton>
                <ToggleButton value="flat" sx={{ px: 1.5, fontSize: '0.75rem', textTransform: 'none' }}>$</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <SummaryValue variant="body1" color="error.main" fontWeight={500}>
              -{formatCurrency(invoice.discount_amount)}
            </SummaryValue>
          </Box>

          {/* Tax */}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 2, fontWeight: 500 }}>Tax Rate:</Typography>
              <NumberTextField
                type="number"
                value={invoice.tax_rate || ''}
                onChange={(e) => canEdit && onUpdate({ tax_rate: e.target.value })}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                size="small" disabled={!canEdit}
                sx={{ width: '130px', opacity: canEdit ? 1 : 0.6 }}
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Box>
            <SummaryValue variant="body1" fontWeight={500}>+{formatCurrency(invoice.tax_amount)}</SummaryValue>
          </Box>

          {/* Shipping */}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 2, fontWeight: 500 }}>Shipping:</Typography>
              <NumberTextField
                type="number"
                value={invoice.shipping_fee || ''}
                onChange={(e) => canEdit && onUpdate({ shipping_fee: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                size="small" disabled={!canEdit}
                sx={{ width: '130px', opacity: canEdit ? 1 : 0.6 }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>
            <SummaryValue variant="body1" fontWeight={500}>+{formatCurrency(invoice.shipping_fee)}</SummaryValue>
          </Box>

          {/* Handling */}
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 2, fontWeight: 500 }}>Handling:</Typography>
              <NumberTextField
                type="number"
                value={invoice.handling_fee || ''}
                onChange={(e) => canEdit && onUpdate({ handling_fee: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                size="small" disabled={!canEdit}
                sx={{ width: '130px', opacity: canEdit ? 1 : 0.6 }}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Box>
            <SummaryValue variant="body1" fontWeight={500}>+{formatCurrency(invoice.handling_fee)}</SummaryValue>
          </Box>

          <Divider sx={{ my: 2, borderWidth: '1px', borderColor: alpha(theme.palette.grey[400], 0.8) }} />

          <TotalRow>
            <Typography variant="h6" fontWeight="500" sx={{ color: 'primary.main' }}>Total:</Typography>
            <Typography variant="h6" color="primary.main" fontWeight="500">{formatCurrency(invoice.total_before_credit)}</Typography>
          </TotalRow>
        </Grid>
      </Grid>
    </SectionPaper>
  );
};

export default SummarySection;
